import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Menu, X, ShoppingCart, Globe } from 'lucide-react';
import { Link, NavLink } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { THUISBEZORGD_ORDER_URL } from '@/components/OrderModal';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.menu'), path: '/menu' },
    { name: t('nav.catering'), path: '/catering' },
    { name: t('nav.marketDay'), path: '/market-day' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  const toggleLanguage = () => {
    setLanguage(language === 'nl' ? 'en' : 'nl');
  };

  const languageButtonTextColor = "#F39B0B";

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-gradient-to-r from-green-400 to-primary shadow-md"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="flex items-center"
            >
              <img src="/images/amsfoodmarket-logo.png" alt="Ams Food Market Logo" className="h-12 md:h-14 mr-2" />
              <span className="text-xl md:text-2xl font-bold text-white hidden sm:inline">Ams Food Market</span>
            </motion.div>
          </Link>

          <nav className="hidden md:flex items-center space-x-1">
            {navLinks.map((link, index) => (
              <motion.div
                key={link.name}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="relative" 
              >
                <NavLink
                  to={link.path}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-medium transition-colors duration-200 ease-in-out rounded-md ${
                      isActive
                        ? 'text-white font-bold bg-white/10'
                        : 'text-white hover:text-green-200 hover:bg-white/5'
                    }`
                  }
                >
                  {link.name}
                </NavLink>
              </motion.div>
            ))}
          </nav>

          <div className="flex items-center space-x-2 md:space-x-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                aria-label="Change language"
                className="hover:bg-transparent focus-visible:bg-transparent hidden md:inline-flex px-2"
                style={{ color: languageButtonTextColor }}
              >
                <span className="text-xs" style={{ color: languageButtonTextColor }}>
                  {language === 'nl' ? 'Switch to EN' : 'Switch to NL'}
                </span>
              </Button>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <Button 
                asChild
                variant="outline" 
                size="sm" 
                className="hidden md:inline-flex bg-white text-primary border-white hover:bg-white hover:text-primary hover:border-white"
              >
                <a href={THUISBEZORGD_ORDER_URL} target="_blank" rel="noopener noreferrer">
                  <ShoppingCart className="h-4 w-4 mr-2 text-primary" />
                  {t('nav.orderNow')}
                </a>
              </Button>
            </motion.div>
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleMobileMenu}
                aria-label="Menu openen/sluiten"
                className="text-white hover:text-green-200 hover:bg-white/10"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="md:hidden bg-primary/95 backdrop-blur-md shadow-lg"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="container mx-auto px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <NavLink
                  key={link.name}
                  to={link.path}
                  className={({ isActive }) =>
                    `block py-3 px-3 text-base font-medium transition-colors rounded-md ${
                      isActive
                        ? 'text-white font-bold bg-white/20'
                        : 'text-white hover:text-green-200 hover:bg-white/10'
                    }`
                  }
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </NavLink>
              ))}
              <div className="pt-2">
                <Button
                  variant="ghost"
                  onClick={() => { toggleLanguage(); setIsMobileMenuOpen(false); }}
                  aria-label="Change language"
                  className="w-full hover:bg-transparent focus-visible:bg-transparent flex items-center justify-center py-3"
                  style={{ color: languageButtonTextColor }}
                >
                  <Globe size={20} className="mr-2" style={{ color: languageButtonTextColor }} />
                  <span style={{ color: languageButtonTextColor }}>
                    {language === 'nl' ? 'Switch to English' : 'Schakel naar Nederlands'}
                  </span>
                </Button>
              </div>
              <div className="pt-2">
                <Button 
                  asChild
                  className="w-full bg-white text-primary hover:bg-green-100"
                >
                  <a
                    href={THUISBEZORGD_ORDER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    {t('nav.orderNow')}
                  </a>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </header>
  );
};

export default Navbar;